// Components/Services/HOACommercial/HOAPortfolio.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const images = [
  { src: "/Images/HOA/HOA 1.jpeg", label: "HOA Project" },
  { src: "/Images/HOA/HOA 2.jpeg", label: "HOA Project" },
  { src: "/Images/HOA/HOA 3.jpeg", label: "HOA Project" },
  { src: "/Images/HOA/HOA 4.jpeg", label: "HOA Project" },
  { src: "/Images/HOA/HOA 5.jpeg", label: "HOA Project" },
  { src: "/Images/HOA/HOA 6.jpeg", label: "HOA Project" },
  { src: "/Images/HOA/HOA 7.jpeg", label: "HOA Project" },
  { src: "/Images/HOA/HOA 8.jpeg", label: "HOA Project" },
];

const GAP = 16;

const HOAPortfolio = () => {
  const [animate, setAnimate] = useState(true);
  const [lightbox, setLightbox] = useState(null);
  const [visible, setVisible] = useState(3);
  const paused = useRef(false);
  const sliderRef = useRef(null);

  // Detect screen size
  useEffect(() => {
    const update = () => {
      setVisible(window.innerWidth < 640 ? 1 : 3);
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  const tripled = [...images, ...images, ...images];
  const START = images.length;

  const [internalIndex, setInternalIndex] = useState(START);

  const getOffset = useCallback((idx) => {
    if (!sliderRef.current) return 0;
    const containerWidth = sliderRef.current.offsetWidth;
    const cardWidth = (containerWidth - GAP * (visible - 1)) / visible;
    return idx * (cardWidth + GAP);
  }, [visible]);

  const next = useCallback(() => {
    setAnimate(true);
    setInternalIndex((i) => i + 1);
  }, []);

  const prev = () => {
    setAnimate(true);
    setInternalIndex((i) => i - 1);
  };

  // Reset internal index when visible changes
  useEffect(() => {
    setAnimate(false);
    setInternalIndex(START);
  }, [visible, START]);

  // Auto-play
  useEffect(() => {
    const id = setInterval(() => {
      if (!paused.current) next();
    }, 4000);
    return () => clearInterval(id);
  }, [next]);

  // Infinite loop snap-back
  useEffect(() => {
    if (internalIndex >= tripled.length - visible) {
      setTimeout(() => {
        setAnimate(false);
        setInternalIndex(START);
      }, 700);
    } else if (internalIndex < 0) {
      setTimeout(() => {
        setAnimate(false);
        setInternalIndex(images.length - 1);
      }, 700);
    }
  }, [internalIndex, visible, tripled.length, START]);

  const dotIndex = (internalIndex - START + images.length) % images.length;
  const totalDots = Math.ceil(images.length / visible);
  const activeDot = Math.floor(dotIndex / visible);

  return (
    <section
      className="bg-[#f8f9fa] py-14 px-6"
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
    >
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <motion.div
          className="text-center mb-10"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <p className="text-[#6c757d] uppercase tracking-widest text-xs font-bold mb-2">
            Our Work
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-[#212529]">
            HOA & Commercial Gallery
          </h2>
        </motion.div>

        {/* Slider */}
        <div className="overflow-hidden rounded-2xl" ref={sliderRef}>
          <div
            className="flex"
            style={{
              gap: `${GAP}px`,
              transform: sliderRef.current
                ? `translateX(-${getOffset(internalIndex)}px)`
                : "translateX(0)",
              transition: animate
                ? "transform 0.7s cubic-bezier(0.76, 0, 0.24, 1)"
                : "none",
            }}
          >
            {tripled.map((img, i) => (
              <div
                key={i}
                className="relative overflow-hidden rounded-2xl group cursor-pointer flex-shrink-0"
                style={{
                  width: sliderRef.current
                    ? `${(sliderRef.current.offsetWidth - GAP * (visible - 1)) / visible}px`
                    : `${100 / visible}%`,
                  aspectRatio: "4/3",
                }}
                onClick={() => setLightbox(img)}
              >
                <img
                  src={img.src}
                  alt={img.label}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all duration-300 flex items-end p-4">
                  <p className="text-white text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-300 translate-y-2 group-hover:translate-y-0">
                    {img.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4 mt-6">
          <button
            onClick={prev}
            className="w-9 h-9 rounded-full border border-[#dee2e6] bg-white flex items-center justify-center text-[#495057] hover:bg-[#212529] hover:text-white hover:border-[#212529] transition duration-300 text-lg"
          >
            ‹
          </button>
          <div className="flex gap-2">
            {Array.from({ length: totalDots }).map((_, i) => (
              <button
                key={i}
                onClick={() => { setAnimate(true); setInternalIndex(START + i * visible); }}
                className={`rounded-full transition-all duration-300 ${i === activeDot ? "w-6 h-2 bg-[#212529]" : "w-2 h-2 bg-[#dee2e6]"}`}
              />
            ))}
          </div>
          <button
            onClick={next}
            className="w-9 h-9 rounded-full border border-[#dee2e6] bg-white flex items-center justify-center text-[#495057] hover:bg-[#212529] hover:text-white hover:border-[#212529] transition duration-300 text-lg"
          >
            ›
          </button>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            className="fixed inset-0 bg-black/90 flex flex-col items-center justify-center z-50 px-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setLightbox(null)}
          >
            <motion.img
              src={lightbox.src}
              alt={lightbox.label}
              className="max-h-[80vh] max-w-[90vw] object-contain rounded-xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ duration: 0.3 }}
            />
            <p className="text-white/70 text-sm mt-4 text-center">{lightbox.label}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default HOAPortfolio;